<template>
  <Layout>
    <div class="contact-page">
      <!-- Hero Section -->
      <div class="contact-hero bg-gradient-primary text-white py-5">
        <div class="container-fluid">
          <div class="row align-items-center">
            <div class="col-lg-6">
              <h1 class="display-4 font-weight-bold mb-4">Let's Connect</h1>
              <p class="lead mb-4">We're here to help and answer any questions you might have.</p>
              <div class="d-flex flex-wrap">
                <b-button variant="light" class="mr-3 mb-3" @click="scrollToForm">
                <i class="mdi mdi-chat mr-2"></i>
                  Send a Message
                </b-button>
                <b-button variant="outline-light" class="mb-3 ms-3" href="tel:+971525495829">
                <i class="mdi mdi-phone mr-2"></i>
                  Call Us
                </b-button>
              </div>
            </div>
            <div class="col-lg-6 d-none d-lg-block">
              <div class="contact-illustration">
                <img src="@/assets/images/contact.png" alt="Contact Illustration" class="img-fluid">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="container my-5">
        <div class="row">
          <!-- Contact Information -->
          <div class="col-lg-4 mb-5 mb-lg-0">
            <b-card no-body class="border-0 shadow-lg contact-info-card">
              <b-card-body>
                <div class="text-center mb-4">
                  <div class="icon-circle bg-primary text-white mb-3">
                    <i class="mdi mdi-home" icon="geo-alt" font-scale="1.5"></i>
                  </div>
                  <h4 class="font-weight-bold">Our Location</h4>
                  <p class="text-muted">OVVURA – Unlock Radiance!</p>
                  <p class="mb-0">Le Pont 2, Jumeirah Dubai, UAE</p>
                </div>

                <hr class="my-4">

                <div class="contact-method mb-4">
                  <div class="icon-circle bg-primary text-white">
                    <i class="mdi mdi-phone"></i>
                  </div>
                  <div class="ml-3">
                    <h5 class="font-weight-bold mb-1">Phone</h5>
                    <p class="mb-0">+971 52 549 5829</p>
                  </div>
                </div>

                <div class="contact-method mb-4">
                  <div class="icon-circle bg-primary text-white">
                    <i class="mdi mdi-email"></i>
                  </div>
                  <div class="ml-3">
                    <h5 class="font-weight-bold mb-1">Email</h5>
                    <p class="mb-0">info@ovvura.ae</p>
                  </div>
                </div>

                <div class="contact-method">
                  <div class="icon-circle bg-primary text-white">
                    <i class="mdi mdi-clock"></i>
                  </div>
                  <div class="ml-3">
                    <h5 class="font-weight-bold mb-1">Business Hours</h5>
                    <p class="mb-1">Sunday – Thursday: 10:00 AM – 7:00 PM</p>
                    <p class="mb-0">Friday – Saturday: Closed</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Social Media -->
            <div class="mt-4 text-center">
              <h5 class="font-weight-bold mb-3">Follow Us</h5>
              <div class="d-flex justify-content-center">
                <b-button variant="outline-primary" class="social-btn rounded-circle mr-2" @click="openSocial('instagram')">
                  <i class="mdi mdi-instagram"></i>
                </b-button>
                <b-button variant="outline-primary" class="social-btn rounded-circle mr-2" @click="openSocial('facebook')">
                  <i class="mdi mdi-facebook"></i>
                </b-button>
                <b-button variant="outline-primary" class="social-btn rounded-circle mr-2" @click="openSocial('twitter')">
                  <i class="mdi mdi-twitter"></i>
                </b-button>
                <b-button variant="outline-primary" class="social-btn rounded-circle" @click="openSocial('tiktok')">
                  <i class="mdi mdi-video"></i>
                </b-button>
              </div>
            </div>
          </div>

          <!-- Contact Form -->
          <div class="col-lg-8" id="contact-form">
            <b-card no-body class="border-0 shadow-lg">
              <b-card-header class="bg-primary text-white">
                <h3 class="mb-0 text-white">Send Us a Message</h3>
              </b-card-header>
              <b-card-body>
                <b-form @submit.prevent="submitForm">
                  <div class="row">
                    <div class="col-md-6">
                      <b-form-group label="Full Name" label-for="full-name" label-class="font-weight-bold">
                        <b-form-input 
                          id="full-name" 
                          v-model="form.fullName" 
                          required 
                          placeholder="Your name"
                          class="form-control-lg">
                        </b-form-input>
                      </b-form-group>
                    </div>
                    <div class="col-md-6">
                      <b-form-group label="Email Address" label-for="email" label-class="font-weight-bold">
                        <b-form-input 
                          id="email" 
                          type="email" 
                          v-model="form.email" 
                          required 
                          placeholder="Your email"
                          class="form-control-lg">
                        </b-form-input>
                      </b-form-group>
                    </div>
                  </div>

                  <b-form-group label="Phone Number" label-for="phone" label-class="font-weight-bold">
                    <b-form-input 
                      id="phone" 
                      type="tel" 
                      v-model="form.phone" 
                      placeholder="Your phone number"
                      class="form-control-lg">
                    </b-form-input>
                  </b-form-group>

                  <b-form-group label="Message" label-for="message" label-class="font-weight-bold">
                    <b-form-textarea 
                      id="message" 
                      v-model="form.message" 
                      rows="5" 
                      required 
                      placeholder="How can we help you?"
                      class="form-control-lg">
                    </b-form-textarea>
                  </b-form-group>

                  <div class="text-right">
                    <b-button 
                      type="submit" 
                      variant="primary" 
                      size="lg" 
                      :disabled="loading"
                      class="px-5 mt-2">
                      <span v-if="!loading">Send Message</span>
                      <b-spinner small v-if="loading"></b-spinner>
                    </b-button>
                  </div>
                </b-form>
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>

      <!-- Map Section -->
      <div class="container-fluid px-0">
        <div class="map-container">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.178509745415!2d55.27072241500887!3d25.19762298389413!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43348a67e24b%3A0xff45e502e1ceb7e2!2sBurj%20Khalifa!5e0!3m2!1sen!2sae!4v1620000000000!5m2!1sen!2sae" 
            width="100%" 
            height="450" 
            style="border:0;" 
            allowfullscreen="" 
            loading="lazy">
          </iframe>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";

export default {
  components: { Layout },
  data() {
    return {
      form: {
        fullName: '',
        email: '',
        phone: '',
        message: ''
      },
      loading: false
    }
  },
  methods: {
    submitForm() {
      this.loading = true;
      
      // Simulate API call
      setTimeout(() => {
        this.loading = false;
        this.$bvToast.toast('Your message has been sent successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-center'
        });
        this.resetForm();
      }, 1500);
    },
    resetForm() {
      this.form = {
        fullName: '',
        email: '',
        phone: '',
        message: ''
      };
    },
    openSocial(platform) {
      const urls = {
        instagram: 'https://instagram.com/ovvura',
        facebook: 'https://facebook.com/ovvura',
        twitter: 'https://twitter.com/ovvura',
        tiktok: 'https://tiktok.com/@ovvura'
      };
      window.open(urls[platform], '_blank');
    },
    scrollToForm() {
      const element = document.getElementById('contact-form');
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
</script>

<style scoped>
.contact-page {
  overflow-x: hidden;
}

.contact-hero {
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  padding: 5rem 0;
}

.contact-illustration {
  animation: float 6s ease-in-out infinite;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.contact-method {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.contact-info-card {
  border-radius: 15px;
  overflow: hidden;
}

.social-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.map-container {
  position: relative;
  overflow: hidden;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
}

.map-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
}

.form-control-lg {
  border-radius: 10px;
  padding: 1rem 1.25rem;
  border: 1px solid #e0e0e0;
  transition: all 0.3s;
}

.form-control-lg:focus {
  border-color: #6e8efb;
  box-shadow: 0 0 0 0.2rem rgba(110, 142, 251, 0.25);
}

@media (max-width: 768px) {
  .contact-hero {
    padding: 3rem 0;
    text-align: center;
  }
  
  .contact-hero .btn {
    width: 100%;
    margin-bottom: 10px;
  }
}
</style>